import React, { useEffect, useRef, useState } from 'react'
import styled, { ThemeProvider } from 'styled-components'
import { Link } from 'gatsby'
import Logo from './logo'
import UnstyledButton from 'components/button/unstyled-button'
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown'
import { FiMenu } from '@react-icons/all-files/fi/FiMenu'
import { IoIosSearch } from '@react-icons/all-files/io/IoIosSearch'
import { MdClose } from '@react-icons/all-files/md/MdClose'
import useMedia from 'react-use/lib/useMedia'
import MobileNav from './mobile-nav'
import raf from 'raf'
import { headerAnimationDuration } from '.'
import isDefined from 'utils/is-defined'
import { useEffectOnce } from 'hooks/use-effect-once'
import { useIsMounted } from 'hooks/use-is-mounted'
import { OutboundLink } from 'gatsby-plugin-google-gtag'

const MainNav = () => {
  const selectorCache = useRef(null)
  const isMounted = useIsMounted()
  const [dropdownActive, setDropdownActive] = useState()
  const [currentDropdownPosition, setCurrentDropdownPosition] = useState(0)
  const [desktopToggleAnimationActive, setDesktopToggleAnimationActive] = useState(false)
  const [mobileMenuActive, setMobileMenuActive] = useState(false)
  const [mobileToggleAnimationActive, setMobileToggleAnimationActive] = useState(false)

  const isDesktop = useMedia('(min-width: 900px)')

  useEffectOnce(() => {
    const newSelectorCache = {}
    // Cache DOM elements
    const selectors = [
      'header-equipment-dropdown',
      'header-equipment-dropdown-inner',
      'header-parts-service-dropdown',
      'header-parts-service-dropdown-inner',
      'header-resources-dropdown',
      'header-resources-dropdown-inner',
      'main-nav-wrapper',
    ]

    selectors.forEach(sel => {
      newSelectorCache[sel] = document.getElementById(sel)
    })

    selectorCache.current = newSelectorCache
  })

  useEffect(() => {
    if (isMounted()) {
      if (!isDesktop) {
        if (dropdownActive) {
          closeDropdown(dropdownActive)
        } else {
          selectorCache.current['main-nav-wrapper'].style.height = '81px'
        }
      } else {
        selectorCache.current['main-nav-wrapper'].style.height = '100px'
      }
    }
  }, [isDesktop])

  const getElInitialHeight = (el, cb) => {
    const h = el.scrollHeight

    if (h === 0) {
      raf(() => {
        if (isMounted()) {
          getElInitialHeight(el)
        }
      })
    } else {
      if (cb) {
        return cb(h)
      }
      return h
    }
  }

  const animateDropdownChange = (newDropdownId, newDropdownPosition) => {
    setDesktopToggleAnimationActive(true)

    let transitionDirection = 'right'
    let transitionDirectionOpposite = 'left'

    if (newDropdownPosition < currentDropdownPosition) {
      transitionDirection = 'left'
      transitionDirectionOpposite = 'right'
    }

    getElInitialHeight(selectorCache.current[dropdownActive], currentDropdownHeight => {
      selectorCache.current[newDropdownId].style.height = currentDropdownHeight

      selectorCache.current[newDropdownId].classList.add(
        'new-dropdown-initial',
        `new-dropdown-initial-translate-${transitionDirection}`
      )

      selectorCache.current[newDropdownId].classList.remove('display-none')

      raf(() => {
        getElInitialHeight(selectorCache.current[newDropdownId], newDropdownHeight => {
          selectorCache.current[dropdownActive].style.height = `${newDropdownHeight}px`
          selectorCache.current[newDropdownId].style.height = `${newDropdownHeight}px`
          selectorCache.current['main-nav-wrapper'].style.height = `${newDropdownHeight + 100}px`

          selectorCache.current[newDropdownId].classList.add('dropdown-menu-active')
          selectorCache.current[newDropdownId].classList.remove(
            'new-dropdown-initial',
            `new-dropdown-initial-translate-${transitionDirection}`
          )

          selectorCache.current[dropdownActive].classList.add(
            `old-dropdown-translate-${transitionDirectionOpposite}`
          )
          selectorCache.current[dropdownActive].classList.remove('dropdown-menu-active')

          setTimeout(() => {
            raf(() => {
              if (isMounted()) {
                selectorCache.current[dropdownActive].classList.add('display-none')
                selectorCache.current[dropdownActive].classList.remove(
                  `old-dropdown-translate-${transitionDirectionOpposite}`
                )

                selectorCache.current[dropdownActive].style.height = 0

                setDesktopToggleAnimationActive(false)
                setDropdownActive(newDropdownId)
                setCurrentDropdownPosition(newDropdownPosition)
              }
            })
          }, headerAnimationDuration)
        })
      })
    })
  }

  const openDropdown = (dropdownId, dropdownPosition) => {
    setDesktopToggleAnimationActive(true)

    selectorCache.current[dropdownId].classList.remove('display-none')

    raf(() => {
      getElInitialHeight(selectorCache.current[dropdownId], newDropdownHeight => {
        selectorCache.current['main-nav-wrapper'].style.height = `${newDropdownHeight + 100}px`
        selectorCache.current[dropdownId].style.height = `${newDropdownHeight}px`

        selectorCache.current[dropdownId].classList.add('dropdown-menu-active')

        setTimeout(() => {
          if (isMounted()) {
            setDesktopToggleAnimationActive(false)
            setDropdownActive(dropdownId)
            setCurrentDropdownPosition(dropdownPosition)
          }
        }, headerAnimationDuration)
      })
    })
  }

  const closeDropdown = dropdownId => {
    setDesktopToggleAnimationActive(true)

    raf(() => {
      selectorCache.current[dropdownId].style.height = 0
      selectorCache.current['main-nav-wrapper'].style.height = isDesktop ? '100px' : '81px'

      selectorCache.current[dropdownId].classList.remove('dropdown-menu-active')

      setTimeout(() => {
        raf(() => {
          if (isMounted()) {
            selectorCache.current[dropdownId].classList.add('display-none')

            setDesktopToggleAnimationActive(false)
            setDropdownActive()
            setCurrentDropdownPosition()
          }
        })
      }, headerAnimationDuration)
    })
  }

  const toggleDropdown = e => {
    if (desktopToggleAnimationActive) {
      return
    }
    const dropdownId = e.currentTarget.getAttribute('data-dropdown-id')
    const dropdownPosition = e.currentTarget.getAttribute('data-dropdown-position')

    if (!isDefined(dropdownActive)) {
      openDropdown(dropdownId, dropdownPosition)
    } else {
      if (dropdownActive === dropdownId) {
        closeDropdown(dropdownId)
      } else {
        animateDropdownChange(dropdownId, dropdownPosition)
      }
    }
  }

  const toggleMobileMenu = () => {
    if (!mobileToggleAnimationActive) {
      setMobileMenuActive(!mobileMenuActive)
    }
  }
  return (
    <ThemeProvider theme={{ dropdownActive: !!dropdownActive }}>
      <MainNavContainer>
        <MainNavWrapper id='main-nav-wrapper'>
          <MainNavInner>
            <Logo />
            <Nav>
              <NavList>
                <li>
                  <TopLevelLink as={Link} to='/'>
                    Home
                  </TopLevelLink>
                </li>
                <li>
                  <TopLevelDropdownLink
                    onClick={toggleDropdown}
                    dropdownActive={dropdownActive}
                    dropdownId='header-equipment-dropdown'
                    dropdownPosition={1}
                  >
                    <TopLevelLinkText>New Equipment</TopLevelLinkText>
                  </TopLevelDropdownLink>
                  <DropdownMenu id='header-equipment-dropdown' className='display-none'>
                    <DropdownMenuInner id='header-equipment-dropdown-inner'>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>John Deere Equipment</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/lawn-garden/'>Lawn &amp; Garden</Link>
                          </li>
                          <li>
                            <Link to='/agriculture/'>Agriculture</Link>
                          </li>
                          <li>
                            <Link to='/construction/'>Construction</Link>
                          </li>
                          <li>
                            <Link to='/lawn-garden/compact-tractors/hutson-tractor-packages/'>
                              Tractor Packages
                            </Link>
                          </li>
                          {/* <li>
                            <OutboundLink href='https://build.hutsoninc.com'>
                              Build Your Own
                            </OutboundLink>
                          </li> */}
                          <li>
                            <Link to='/government-sales/'>Government</Link>
                          </li>
                          <li>
                            <Link to='/equipment/'>All Equipment</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Product Lines</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/manitou/'>Manitou</Link>
                          </li>
                          <li>
                            <Link to='/stihl/'>STIHL</Link>
                          </li>
                          <li>
                            <Link to='/product-lines/'>All Product Lines</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Other</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/implements/'>Implements</Link>
                          </li>
                          <li>
                            <Link to='/government-sales/'>Government</Link>
                          </li>
                          <li>
                            <Link to='/promotions/john-deere/'>John Deere Promotions</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                    </DropdownMenuInner>
                  </DropdownMenu>
                </li>
                <li>
                  <TopLevelLink as={Link} to='/used-equipment/'>
                    Used Equipment
                  </TopLevelLink>
                </li>
                <li>
                  <TopLevelDropdownLink
                    onClick={toggleDropdown}
                    dropdownActive={dropdownActive}
                    dropdownId='header-parts-service-dropdown'
                    dropdownPosition={2}
                  >
                    <TopLevelLinkText>Parts &amp; Service</TopLevelLinkText>
                  </TopLevelDropdownLink>
                  <DropdownMenu id='header-parts-service-dropdown' className='display-none'>
                    <DropdownMenuInner id='header-parts-service-dropdown-inner'>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Parts</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/parts/'>Parts at Hutson</Link>
                          </li>
                          <li>
                            <OutboundLink href='https://shop.deere.com/us/?dealer-id=034321&CID=LNK_DLR_enUS_OS_HutsonWebsite'>
                              Shop Parts Online
                            </OutboundLink>
                          </li>
                          <li>
                            <Link to='/hutson-customer-portal/'>Hutson Customer Portal</Link>
                          </li>
                          <li>
                            <Link to='/promotions/parts/'>Parts Promotions</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Service</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/service/'>Service at Hutson</Link>
                          </li>
                          <li>
                            <Link to='/service/schedule-service/'>Schedule Service</Link>
                          </li>
                          <li>
                            <Link to='/service/inspections/'>Inspection Programs</Link>
                          </li>
                          <li>
                            <Link to='/promotions/service/'>Service Promotions</Link>
                          </li>
                          <li>
                            <Link to='/powergard/'>PowerGard Protection Plan</Link>
                          </li>
                          <li>
                            <Link to='/sentry/'>Sentry Insurance</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Support</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/connected-support/'>Connected Support</Link>
                          </li>
                          <li>
                            <Link to='/precision-ag/'>Precision Ag</Link>
                          </li>
                          <li>
                            <Link to='/after-hours-support/'>After Hours Support</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                    </DropdownMenuInner>
                  </DropdownMenu>
                </li>
                <li>
                  <TopLevelDropdownLink
                    onClick={toggleDropdown}
                    dropdownActive={dropdownActive}
                    dropdownId='header-resources-dropdown'
                    dropdownPosition={3}
                  >
                    <TopLevelLinkText>Resources</TopLevelLinkText>
                  </TopLevelDropdownLink>
                  <DropdownMenu id='header-resources-dropdown' className='display-none'>
                    <DropdownMenuInner id='header-resources-dropdown-inner'>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Articles</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/blog/'>Blog</Link>
                          </li>
                          <li>
                            <Link to='/knowledge-center/'>Knowledge Center</Link>
                          </li>
                          <li>
                            <OutboundLink href='https://fieldguide.hutsoninc.com'>
                              Field Guide
                            </OutboundLink>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>Reach Us</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/locations/'>Locations</Link>
                          </li>
                          <li>
                            <Link to='/contact/'>Contact Us</Link>
                          </li>
                          <li>
                            <Link to='/careers/'>Careers</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                      <DropdownMenuColumn>
                        <DropdownMenuHeading>The Hutson Advantage</DropdownMenuHeading>
                        <DropdownMenuNavList>
                          <li>
                            <Link to='/about/'>About Us</Link>
                          </li>
                          <li>
                            <Link to='/promotions/'>Promotions</Link>
                          </li>
                        </DropdownMenuNavList>
                      </DropdownMenuColumn>
                    </DropdownMenuInner>
                  </DropdownMenu>
                </li>
                <li>
                  <TopLevelLink as={Link} to='/search/' aria-label='Navigate to search page'>
                    <SearchIcon aria-hidden='true' focusable='false' role='presentation' />
                  </TopLevelLink>
                </li>
              </NavList>
            </Nav>
            <MobileMenuButtonContainer>
              <MobileMenuButton
                onClick={toggleMobileMenu}
                aria-label='Menu'
                aria-expanded={mobileMenuActive}
                aria-controls='mobile-nav-menu'
              >
                {mobileMenuActive ? (
                  <MdClose aria-hidden='true' focusable='false' role='presentation' />
                ) : (
                  <FiMenu aria-hidden='true' focusable='false' role='presentation' />
                )}
              </MobileMenuButton>
            </MobileMenuButtonContainer>
          </MainNavInner>
        </MainNavWrapper>
        <MobileNav
          active={mobileMenuActive}
          id='mobile-nav-menu'
          setIsAnimating={setMobileToggleAnimationActive}
        />
      </MainNavContainer>
    </ThemeProvider>
  )
}

const MainNavContainer = styled.div`
  height: auto;
  min-height: 80px;
  width: 100%;
  z-index: 9000;

  @media (min-width: 900px) {
    min-height: 100px;
    padding: 0 25px;
    position: absolute;
    top: ${props => (props.theme.locationMenuActive ? '80px' : '40px')};
    transition: ${props => props.theme.transition};
  }
`

const MainNavWrapper = styled.div`
  background-color: #fff;
  border-bottom: 1px solid ${props => props.theme.color.n40};
  box-sizing: border-box;

  @media (min-width: 900px) {
    border-bottom: 0;
    border-radius: 2px;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.15);
    height: 100px;
    transition: ${props => props.theme.transition};
  }
`

const MainNavInner = styled.div`
  background-color: #fff;
  box-sizing: border-box;
  height: 80px;
  padding: 0 15px;
  position: relative;

  @media (min-width: 900px) {
    height: 100px;
  }
`

const Nav = styled.nav`
  display: none;
  height: 100%;
  left: 0;
  padding: 0 15px 0 0;
  position: absolute;
  right: 0;
  text-align: right;
  width: 100%;
  z-index: 0;

  @media (min-width: 900px) {
    display: block;
  }
`

const NavList = styled.ul`
  background-color: #fff;
  height: 100%;
  list-style: none;
  margin: 0;
  padding: 0;

  > li {
    box-sizing: border-box;
    display: inline-block;
    height: 100%;
    margin: 0;
    padding: 0;
    position: static;
    vertical-align: top;
  }
`

const TopLevelLink = styled(UnstyledButton)`
  color: #303030;
  display: inline-block;
  font-size: 18px;
  font-weight: 600;
  line-height: 1.15;
  padding: 15px;
  position: relative;
  text-decoration: none;
  top: 50%;
  transform: translateY(-50%);
  vertical-align: middle;

  :hover,
  :focus {
    text-decoration: underline;
  }
`

const TopLevelLinkText = styled.span`
  display: inline;
  vertical-align: middle;
`

const TopLevelLinkArrow = ({ active }) => {
  return (
    <TopLevelLinkArrowIcon
      active={active}
      aria-hidden='true'
      focusable='false'
      role='presentation'
    />
  )
}

const TopLevelLinkArrowIcon = styled(FiChevronDown)`
  color: #303030;
  font-size: 0.65em;
  margin-left: 5px;
  transform: ${props => (props.active ? 'rotate(-180deg)' : 'none')};
  transition: ${props => props.theme.transition};
  vertical-align: middle;
`

const TopLevelDropdownLink = ({
  children,
  dropdownActive,
  dropdownId,
  dropdownPosition,
  ...props
}) => {
  const active = dropdownActive === dropdownId
  return (
    <TopLevelLink
      data-dropdown-id={dropdownId}
      data-dropdown-position={dropdownPosition}
      aria-expanded={active}
      aria-controls={dropdownId}
      {...props}
    >
      {children}
      <TopLevelLinkArrow active={active ? 'active-dropdown' : ''} />
    </TopLevelLink>
  )
}

const SearchIcon = styled(IoIosSearch)`
  color: #303030;
  font-size: 24px;
  margin-top: -1px;
  transform: rotate(90deg);
  vertical-align: middle;
`

const DropdownMenuInner = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  opacity: 0;
  padding: 25px;
  position: relative;
  transform: translate3d(0, 0, 0);
  transition: ${props => props.theme.transition};

  :before {
    background-color: ${props => props.theme.color.n40};
    content: '';
    display: block;
    height: 1px;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
`

const DropdownMenu = styled.div`
  background-color: rgba(250, 250, 250, 1);
  border-radius: 0 0 2px 2px;
  box-sizing: border-box;
  display: block;
  height: 0;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  text-align: left;
  top: 100px;
  transition: ${props => props.theme.transition};
  z-index: 8998;

  &.dropdown-menu-active {
    z-index: 8999;

    ${DropdownMenuInner} {
      opacity: 1;
    }
  }

  &.new-dropdown-initial {
    background-color: rgba(250, 250, 250, 0);
  }

  &.old-dropdown-translate-left {
    ${DropdownMenuInner} {
      transform: translate3d(-25px, 0, 0);
    }
  }

  &.old-dropdown-translate-right {
    ${DropdownMenuInner} {
      transform: translate3d(25px, 0, 0);
    }
  }

  &.new-dropdown-initial-translate-left {
    ${DropdownMenuInner} {
      transform: translate3d(-25px, 0, 0);
    }
  }

  &.new-dropdown-initial-translate-right {
    ${DropdownMenuInner} {
      transform: translate3d(25px, 0, 0);
    }
  }
`

const DropdownMenuColumn = styled.div`
  flex-grow: 1;
  max-width: 33.33%;
  width: auto;

  :not(:last-of-type) {
    border-right: 1px solid ${props => props.theme.color.n40};
    padding-right: 25px;
  }

  :not(:first-of-type) {
    padding-left: 25px;
  }
`

const DropdownMenuHeading = styled.span`
  color: #565454;
  font-size: 18px;
  font-weight: 600;
`

const DropdownMenuNavList = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  list-style: none;
  margin: 4px 0 0;
  padding: 0;

  > li {
    box-sizing: border-box;
    display: inline-block;
    line-height: 1.35;
    margin: 4px 0 0;
    padding: 0 12px 0 0;
    width: 50%;

    a {
      color: #565454;
      font-size: 16px;
      font-weight: 400;
      margin: 0;
      padding: 0;
      text-decoration: none;

      :hover,
      :focus {
        text-decoration: underline;
      }
    }
  }
`

const MobileMenuButtonContainer = styled.div`
  height: 80px;
  position: relative;
  text-align: right;
  width: 100%;

  @media (min-width: 900px) {
    display: none;
  }
`

const MobileMenuButton = styled(UnstyledButton)`
  display: inline-block;
  padding: 10px;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  vertical-align: middle;

  svg {
    color: #303030;
    font-size: 30px;
    vertical-align: middle;
  }

  @media (min-width: 900px) {
    display: none;
  }
`

export default MainNav
